/**
 * Kit de soporte v1 — POST /api/v1/bug-reports (ADR-0025).
 *
 * Multipart: `datos` (JSON) y una `capturas` opcional. Quien llama conserva el
 * mismo `report_id` entre reintentos, así un reenvío completa el mismo ticket.
 * Usa `fetch` directo: no depende del cliente de API de cada app.
 */

import type { SoporteConfig } from "./config";
import {
  recordApiError,
  templatePath,
  type ReportContext,
} from "./supportContext";

export interface BugReportDatos extends ReportContext {
  report_id: string;
  descripcion: string;
  entidad_id: string | null;
}

export interface BugReportAccepted {
  report_id: string;
  folio: string;
  estado: string;
}

/** El envío falló. `status` 0 = no hubo respuesta (red o autenticación local). */
export class EnvioError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "EnvioError";
    this.status = status;
  }
}

export async function enviarReporte(
  config: SoporteConfig,
  datos: BugReportDatos,
  captura: File | null,
): Promise<BugReportAccepted> {
  const form = new FormData();
  form.append("datos", JSON.stringify(datos));
  if (captura) form.append("capturas", captura);

  let response: Response;
  try {
    const headers = (await config.headers?.()) ?? {};
    response = await fetch(config.endpoint, {
      method: "POST",
      body: form,
      headers,
      credentials: config.credentials,
    });
  } catch {
    throw new EnvioError(0, "");
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      detail?: { code?: unknown; message?: unknown };
    } | null;
    const code =
      typeof body?.detail?.code === "string" ? body.detail.code : null;
    const message =
      typeof body?.detail?.message === "string" ? body.detail.message : "";
    recordApiError({
      method: "POST",
      path: templatePath(config.endpoint),
      status: response.status,
      code,
      requestId: response.headers.get("x-request-id"),
    });
    throw new EnvioError(response.status, message);
  }
  return (await response.json()) as BugReportAccepted;
}
